import { Box, Divider } from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import filterIcon from 'assets/icon/filter.svg';
import { CancelBtn, SubmitBtn, useSearchbarStyles } from 'components/FormTitleBar/SearchBar';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { useClickAway } from 'react-use';
import { getDefaultGroup } from 'utils/groups';
import { getAllTypeIds, getSelectedTypeIds } from '../../services/nursingOverviewList';
import SimpleCheckbox from '../CheckboxWithLabel/SimpleCheckbox';
import { Button, SwitchBtn } from '../FormTitleBar';

export type FilterValues = {
  id: string;
  label?: string;
  value: boolean;
  disabled: boolean;
};

export type FilterHelperValues = {
  checkBox: FilterValues;
  group?: Array<string>;
  default?: boolean;
};

type Props = {
  items: FilterValues[];
  helperItems: FilterHelperValues[];
  onFilterChange: (filter: Array<string>) => void;
  disableClickAway?: boolean;
};

const FilterCheckBox: React.VFC<Props> = ({
  items,
  helperItems,
  onFilterChange,
  disableClickAway = true,
}) => {
  const { t } = useTranslation();
  const classes = useSearchbarStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterValues, setFilterValues] = useState<FilterValues[]>([]);
  const [filterHelperValues, setFilterHelperValues] = useState<FilterHelperValues[]>([]);
  const clickAwayRef = useRef(null);

  useEffect(() => {
    setDefaults();
  }, [items]);

  useEffect(() => {
    setFilterHelperValues(helperItems);
  }, [helperItems]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (open) {
      handleClose();
    }
  };

  const onCancelClick = (event: React.MouseEvent<HTMLElement>) => {
    setDefaults();
    onFilterChange(getSelectedTypeIds(filterValues));
    handleFilterClick(event);
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const onSubmit = () => {
    onFilterChange(getSelectedTypeIds(filterValues));
    if (open) {
      handleClose();
    }
  };

  const onBoxChange = (index: number, chvalue: boolean) => {
    const boxes: FilterValues[] = [...filterValues];
    if (boxes[index] !== undefined) {
      boxes[index].value = chvalue;
      setFilterValues(boxes);
    }
    // set the select all
    if (getAllCheckboxesAreSelected()) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
    deselectSelectedGroup();
    selectMatchedGroupName();
  };

  const onHelperBoxChange = (index: number, value: boolean) => {
    if (filterHelperValues) {
      const helperBoxes: FilterHelperValues[] = [...filterHelperValues];
      if (helperBoxes[index] !== undefined) {
        helperBoxes[index].checkBox.value = value;
        setFilterHelperValues(helperBoxes);
      }
      if (helperBoxes[index].checkBox.id === 'check_all') {
        setAllCheckboxes(helperBoxes[index].checkBox.value);
      }
      // when the box is checked true, set all others to false
      if (helperBoxes[index].checkBox.value) {
        filterHelperValues?.forEach((i, indx) => {
          if (index !== indx) {
            i.checkBox.value = false;
          }
        });
      }
      //when group checkbox is checked
      if (helperBoxes[index].checkBox.id !== 'check_all') {
        if (helperBoxes[index].group) {
          setGroup(helperBoxes[index].group, helperBoxes[index].checkBox.value);
        }
      }
    }
  };

  const setDefaults = () => {
    const boxes: FilterValues[] = [...items];
    const helperBoxes: FilterHelperValues[] = [...helperItems];
    const defaults = getDefaultGroup(helperBoxes);
    if (defaults?.length === 0) {
      getAllTypeIds(boxes);
    } else {
      boxes.forEach((i) => {
        if (defaults?.includes(i.id)) {
          i.value = true;
        } else {
          i.value = false;
        }
      });
    }
    setFilterValues(boxes);
    setDefaultGroupName();
  };

  const setDefaultGroupName = () => {
    const helperBoxes: FilterHelperValues[] = [...helperItems];
    helperBoxes?.forEach((i, index) => {
      if (i.default) {
        helperBoxes[index].checkBox.value = true;
      } else {
        helperBoxes[index].checkBox.value = false;
      }
    });
    setFilterHelperValues(helperBoxes);
  };

  const setGroup = (group: Array<string> | undefined, value: boolean) => {
    if (group) {
      const boxes: FilterValues[] = [...filterValues];
      boxes.forEach((i) => {
        if (group.includes(i.id)) {
          i.value = value;
        } else {
          i.value = false;
        }
      });
      setFilterValues(boxes);
    }
  };

  const setGroupName = (index: number) => {
    const helperBoxes: FilterHelperValues[] = [...helperItems];
    helperBoxes[index].checkBox.value = true;
    setFilterHelperValues(helperBoxes);
  };

  const setAllCheckboxes = (value: boolean) => {
    const boxes: FilterValues[] = [...filterValues];
    boxes.forEach((i) => {
      i.value = value;
    });
    setFilterValues(boxes);
  };

  const setSelectAll = (value: boolean) => {
    const helperBoxes: FilterHelperValues[] = [...helperItems];
    helperBoxes?.forEach((i, index) => {
      if (i.checkBox.id === 'check_all') {
        helperBoxes[index].checkBox.value = value;
      }
    });
    setFilterHelperValues(helperBoxes);
  };

  const getAllCheckboxesAreSelected = () => {
    return filterValues.filter((i) => i.value === true).length === filterValues.length;
  };

  const deselectSelectedGroup = () => {
    const helperBoxes: FilterHelperValues[] = [...helperItems];
    helperBoxes.forEach((i) => {
      if (i.checkBox.value && i.checkBox.id !== 'check_all') {
        i.checkBox.value = false;
      }
    });
    setFilterHelperValues(helperBoxes);
  };

  const selectMatchedGroupName = () => {
    const boxes: FilterValues[] = [...filterValues];
    const helperBoxes: FilterHelperValues[] = [...helperItems];
    const getActualSelected = getSelectedTypeIds(boxes);
    helperBoxes?.forEach((i, index) => {
      if (i.group) {
        if (arraysSameContent(getActualSelected, i.group)) {
          setGroupName(index);
        }
      }
    });
  };

  function arraysSameContent(arr1: Array<string>, arr2: Array<string>) {
    if (arr1.length === arr2.length) {
      return arr1.every((element) => {
        if (arr2.includes(element)) {
          return true;
        }
        return false;
      });
    }
    return false;
  }

  useClickAway(
    clickAwayRef,
    () => {
      if (!disableClickAway) {
        handleClose();
      }
    },
    ['click'],
  );

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  return (
    <Box
      ref={clickAwayRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '@media(max-width: 1280px)': {
          flexWrap: 'wrap',
        },
      }}
    >
      <SwitchBtn showSubmit={false} style={{ marginLeft: 0, overflow: 'visible' }}>
        <Button
          id="filter-btn"
          onClick={handleFilterClick}
          style={{
            position: 'relative',
          }}
        >
          <SVG src={filterIcon} />
        </Button>
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          className={classes.popper}
        >
          <Box sx={{ p: '5px' }}>
            <Box>
              {filterHelperValues &&
                filterHelperValues?.map((hitem, index) => (
                  <Box sx={{ p: '5px' }} key={hitem.checkBox.id}>
                    <SimpleCheckbox
                      label={
                        hitem?.checkBox.label ? t(hitem?.checkBox.label) : t(hitem?.checkBox.id)
                      }
                      editable={!hitem?.checkBox.disabled}
                      checkboxProps={{
                        checked: hitem?.checkBox.value,
                        onChange: (e) => onHelperBoxChange(index, e.target.checked),
                      }}
                    />
                  </Box>
                ))}
            </Box>
            <Divider />
            <Box>
              {filterValues &&
                filterValues.map((item, index) => (
                  <Box sx={{ p: '5px' }} key={item.id}>
                    <SimpleCheckbox
                      label={item?.label ? t(item?.label) : t(item?.id)}
                      editable={!item?.disabled}
                      checkboxProps={{
                        checked: item?.value,
                        onChange: (e) => onBoxChange(index, e.target.checked),
                      }}
                    />
                  </Box>
                ))}
            </Box>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <SubmitBtn id="apply-btn" type="button" onClick={onSubmit}>
                {t('apply_btn')}
              </SubmitBtn>
              <CancelBtn id="reset-btn" type="button" onClick={onCancelClick}>
                {t('reset_btn')}
              </CancelBtn>
            </Box>
          </Box>
        </Popper>
      </SwitchBtn>
    </Box>
  );
};

export default FilterCheckBox;
